import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { MessageSquare, Send, User, RefreshCw } from 'lucide-react';

const AdminMessages = () => {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
        setUsers(response.data.filter(u => u.id !== user?.id));
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token && user) {
      fetchUsers();
    }
  }, [token, user?.id]);

  const fetchConversation = async (userId) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    fetchConversation(u.id);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedUser) return;

    try {
      await axios.post(`${API}/messages`, { recipient_id: selectedUser.id, content: newMessage }, { headers: { Authorization: `Bearer ${token}` } });
      setNewMessage('');
      fetchConversation(selectedUser.id);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  return (
    <div className="p-6 h-[calc(100vh-56px)] lg:h-screen">
      <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2 mb-4">
        <MessageSquare className="w-6 h-6 text-amber-500" /> Messages
      </h1>

      <div className="grid lg:grid-cols-3 gap-4 h-[calc(100%-60px)]">
        {/* User List */}
        <Card className="bg-white lg:col-span-1">
          <CardContent className="p-0">
            <div className="p-3 border-b"><h3 className="font-medium text-slate-700">Community Members</h3></div>
            <ScrollArea className="h-[400px] lg:h-[calc(100vh-200px)]">
              {loading ? (
                <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-plp-blue" /></div>
              ) : users.length === 0 ? (
                <p className="text-center py-8 text-slate-500">No users found</p>
              ) : (
                <div className="p-2 space-y-1">
                  {users.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => handleSelectUser(u)}
                      className={`w-full p-3 rounded-lg text-left flex items-center gap-3 transition-colors ${
                        selectedUser?.id === u.id ? 'bg-plp-blue/10 text-plp-blue' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{u.name}</p>
                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Conversation */}
        <Card className="bg-white lg:col-span-2 flex flex-col">
          {selectedUser ? (
            <>
              <div className="p-3 border-b flex items-center gap-3">
                <div className="w-10 h-10 bg-plp-gold rounded-full flex items-center justify-center">
                  <span className="text-plp-blue font-bold">{selectedUser.name.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-medium">{selectedUser.name}</p>
                  <p className="text-xs text-slate-500">{selectedUser.email}</p>
                </div>
              </div>
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                        msg.sender_id === user.id
                          ? 'bg-plp-blue text-white rounded-br-none'
                          : 'bg-slate-100 text-slate-700 rounded-bl-none'
                      }`}>
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-white/60' : 'text-slate-400'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="p-3 border-t">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    className="flex-1"
                  />
                  <Button onClick={handleSend} className="bg-plp-blue"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            </>
          ) : (
            <CardContent className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Select a user to start messaging</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminMessages;
